import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, Card, Col, Dropdown, Grid, message, Modal, Popconfirm, Row, Space, Typography} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EllipsisOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import AgGrid from './common/AgGrid';
import {AG_GRID_LOCALE_TEXT, AG_GRID_PAGE_SIZE_OPTIONS} from '../config/agGridConfig';
import {createTermData, createTermDataBatch, deleteTermDataBatch, getTermDataList} from '../api/termDataApi';
import ExcelUploadModal from './ExcelUploadModal';
import TermDataCreateModal from './TermDataCreateModal';
import SearchComponent from './SearchComponent';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import PermissionGate from './common/PermissionGate';

const {Title} = Typography;

function TermDataList() {
    const [rowData, setRowData] = useState([]);
    const [, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchColumn, setSearchColumn] = useState('all');
    const [deleting, setDeleting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const gridRef = useRef();
    const screens = Grid.useBreakpoint();
    const isXs = !screens.md;

    // API 권한 기반 버튼 제어
    const { canCall, loading: permLoading } = useApiPermission();
    const canRead = canCall(getTermDataList);
    const canCreate = canCall(createTermData);
    const canUpload = canCall(createTermDataBatch);
    const canDeleteBatch = canCall(deleteTermDataBatch);

    const searchOptions = [
        {value: 'all', label: '전체'},
        {value: 'trmdNm', label: '용어명'},
        {value: 'trmExpln', label: '설명'},
        {value: 'trmEngAbbrNm', label: '영문약어'},
        {value: 'domainNm', label: '도메인명'},
        {value: 'synm', label: '동의어'},
        {value: 'rgtr', label: '등록자'},
        {value: 'mdfr', label: '수정자'}
    ];

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    // AG Grid 컬럼 정의
    const agGridColumnDefs = [
        {
            headerName: '',
            field: 'checkbox',
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: true,
            width: 33,
            pinned: 'left',
            lockPosition: true,
            suppressMenu: true,
            sortable: false,
            filter: false,
            resizable: false,
            suppressExport: true,
        },
        {headerName: '용어데이터ID', field: 'trmDataId', hide: true},
        {headerName: '용어명', field: 'trmdNm', minWidth: 120, flex: 0.2},
        {headerName: '설명', field: 'trmExpln', minWidth: 200, flex: 0.8},
        {headerName: '영문약어', field: 'trmEngAbbrNm', width: 100},
        {headerName: '도메인데이터ID', field: 'domainDataId', hide: true},
        {headerName: '도메인명', field: 'domainNm', width: 100},
        {headerName: '허용값', field: 'prmVl', width: 100 },
        {headerName: '저장형식', field: 'strgFrm', width: 100 },
        {headerName: '표시형식', field: 'indctFrm', width: 100 },
        {headerName: '동의어', field: 'synm', width: 100},
        {headerName: '등록자', field: 'rgtr', width: 100},
        {headerName: '등록일시', field: 'regDt', valueFormatter: (params) => formatDateTime(params.value), width: 120},
        {headerName: '수정자', field: 'mdfr', width: 100},
        {headerName: '수정일시', field: 'mdfcnDt', valueFormatter: (params) => formatDateTime(params.value), width: 120}
    ];

    const agGridDefaultColDef = { sortable: true, filter: true, resizable: true };

    const getFilteredData = () => {
        if (!searchText.trim()) return rowData;
        const searchValue = searchText.toLowerCase();
        return rowData.filter((item) => {
            switch (searchColumn) {
                case 'trmdNm':
                    return item.trmdNm && item.trmdNm.toLowerCase().includes(searchValue);
                case 'domainNm':
                    return item.domainNm && item.domainNm.toLowerCase().includes(searchValue);
                case 'trmExpln':
                    return item.trmExpln && item.trmExpln.toLowerCase().includes(searchValue);
                case 'trmEngAbbrNm':
                    return item.trmEngAbbrNm && item.trmEngAbbrNm.toLowerCase().includes(searchValue);
                case 'synm':
                    return item.synm && item.synm.toLowerCase().includes(searchValue);
                case 'rgtr':
                    return item.rgtr && item.rgtr.toLowerCase().includes(searchValue);
                case 'mdfr':
                    return item.mdfr && item.mdfr.toLowerCase().includes(searchValue);
                case 'all':
                default:
                    return (
                        (item.trmdNm && item.trmdNm.toLowerCase().includes(searchValue)) ||
                        (item.trmExpln && item.trmExpln.toLowerCase().includes(searchValue)) ||
                        (item.trmEngAbbrNm && item.trmEngAbbrNm.toLowerCase().includes(searchValue)) ||
                        (item.domainNm && item.domainNm.toLowerCase().includes(searchValue)) ||
                        (item.synm && item.synm.toLowerCase().includes(searchValue)) ||
                        (item.rgtr && item.rgtr.toLowerCase().includes(searchValue)) ||
                        (item.mdfr && item.mdfr.toLowerCase().includes(searchValue))
                    );
            }
        });
    };

    const fetchList = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (permLoading) return;
            if (!canRead) { setError('목록 조회 권한이 없습니다.'); return; }
            const data = await getTermDataList();
            if (data && Array.isArray(data.data)) {
                setRowData(data.data);
            } else {
                setError('응답 데이터 형식이 올바르지 않습니다.');
            }
        } catch (err) {
            setError('데이터를 가져오는 데 실패했습니다: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [permLoading, canRead]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // AG Grid용 삭제 함수
    const deleteSelectedRowsAgGrid = async () => {
        if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
        if (!gridRef.current || !gridRef.current.api) {
            message.warning('그리드가 아직 초기화되지 않았습니다. 잠시 후 다시 시도해주세요.');
            return;
        }
        const selectedNodes = gridRef.current.api.getSelectedNodes();
        if (selectedNodes.length === 0) {
            message.warning('삭제할 항목을 선택해주세요.');
            return;
        }
        const validSelectedNodes = selectedNodes.filter((node) => node.data && node.data.trmDataId);
        const selectedIds = validSelectedNodes.map((node) => node.data.trmDataId);
        if (selectedIds.length === 0) {
            message.warning('삭제할 항목을 선택해주세요.');
            return;
        }
        setDeleting(true);
        try {
            await deleteTermDataBatch(selectedIds);
            await fetchList();
            if (gridRef.current && gridRef.current.api) {
                gridRef.current.api.deselectAll();
            }
            message.success(`${selectedIds.length}개 항목이 삭제되었습니다.`);
        } catch (err) {
            // 에러 토스트는 공통 인터셉터에서 처리
        } finally {
            setDeleting(false);
        }
    };

    const handleExcelUploadSuccess = () => { fetchList(); };
    const handleCreateSuccess = () => { fetchList(); };

    // const handleDownloadCsv = () => {
    //     if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 초기화되지 않았습니다.'); return; }
    //     // 대체: XLSX 내보내기로 변경
    //     handleDownloadExcel();
    // };

    // XLSX 다운로드: 첫 열에 UUID(trmDataId), 표시 컬럼 순서 일치
    const handleDownloadExcel = () => {
        if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 초기화되지 않았습니다.'); return; }
        const displayed = gridRef.current.columnApi
            ? gridRef.current.columnApi.getAllDisplayedColumns().map(col => {
                const def = col.getColDef();
                return { field: def.field, headerName: def.headerName || def.field, suppressExport: !!def.suppressExport };
              }).filter(c => c.field && !c.suppressExport && c.field !== 'trmDataId')
            : agGridColumnDefs.filter(c => c.field && !c.suppressExport && c.field !== 'trmDataId').map(c => ({ field: c.field, headerName: c.headerName || c.field }));
        const header = ['UUID', ...displayed.map(c => c.headerName)];
        const rowsAoA = [header];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
            const dataRow = [node.data?.trmDataId ?? ''];
            displayed.forEach(c => {
                let val = node.data?.[c.field];
                if (c.field === 'regDt' || c.field === 'mdfcnDt') val = formatDateTime(val);
                dataRow.push(val ?? '');
            });
            rowsAoA.push(dataRow);
        });
        const ws = XLSX.utils.aoa_to_sheet(rowsAoA);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Terms');
        XLSX.writeFile(wb, 'terms.xlsx');
    };


    if (error) return <Alert message="일시적인 오류가 발생했습니다." description={error} type="error" showIcon/>;

    // 작은 화면 드롭다운에서 사용할 삭제 확인 모달
    const confirmDeleteFromMenu = () => {
        if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
        if (!gridRef.current || !gridRef.current.api) {
            message.warning('그리드가 아직 초기화되지 않았습니다. 잠시 후 다시 시도해주세요.');
            return;
        }
        const selectedNodes = gridRef.current.api.getSelectedNodes();
        if (selectedNodes.length === 0) {
            message.warning('삭제할 항목을 선택해주세요.');
            return;
        }
        Modal.confirm({
            title: '선택된 항목 삭제',
            content: '선택된 항목들을 삭제하시겠습니까?',
            okText: '삭제',
            okButtonProps: { danger: true },
            cancelText: '취소',
            onOk: deleteSelectedRowsAgGrid,
        });
    };

    const actionMenuItems = [
        ...(canCreate ? [{ key: 'create', icon: <PlusOutlined />, label: '신규생성' }] : []),
        ...(canDeleteBatch ? [{ type: 'divider' }, { key: 'delete', icon: <DeleteOutlined />, label: '선택 항목 삭제', danger: true }] : []),
        ...(canUpload ? [{ type: 'divider' }, { key: 'upload', icon: <UploadOutlined />, label: '엑셀 업로드' }] : []),
        { key: 'download', icon: <DownloadOutlined />, label: '엑셀 다운로드' },
        ...(canRead ? [{ type: 'divider' }, { key: 'refresh', icon: <ReloadOutlined />, label: '새로고침' }] : []),
    ];

    const onActionMenuClick = ({ key }) => {
        switch (key) {
            case 'create':
                if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); break; }
                setIsCreateModalOpen(true);
                break;
            case 'delete':
                confirmDeleteFromMenu();
                break;
            case 'upload':
                if (permLoading || !canUpload) { message.warning('업로드 권한이 없습니다.'); break; }
                setIsExcelModalOpen(true);
                break;
            case 'download':
                handleDownloadExcel();
                break;
            case 'refresh':
                if (permLoading || !canRead) { message.warning('조회 권한이 없습니다.'); break; }
                fetchList();
                break;
            default:
                break;
        }
    };

    return (
        <PermissionGate require={getTermDataList}>
            {({allowed}) => (
                <Card style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
                    <Title level={2}>용어 목록</Title>

                    {successMessage && (
                        <Alert message="성공" description={successMessage} type="success" showIcon closable onClose={() => setSuccessMessage('')} style={{marginBottom: 16}} />
                    )}

                    <Card style={{marginBottom: 16}}>
                        <Row gutter={[8, 8]} align="middle">
                            <Col xs={24}>
                                {isXs ? (
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <SearchComponent
                                                searchColumn={searchColumn}
                                                setSearchColumn={setSearchColumn}
                                                searchText={searchText}
                                                setSearchText={setSearchText}
                                                searchOptions={searchOptions}
                                                defaultSearchColumn={'trmdNm'}
                                            />
                                        </div>
                                        <div style={{ flexShrink: 0 }}>
                                            <Dropdown menu={{ items: actionMenuItems, onClick: onActionMenuClick }} placement="bottomRight" trigger={["click"]}>
                                                <Button aria-label="작업 메뉴" icon={<EllipsisOutlined />} />
                                            </Dropdown>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', width: '100%' }}>
                                        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                                            <SearchComponent
                                                searchColumn={searchColumn}
                                                setSearchColumn={setSearchColumn}
                                                searchText={searchText}
                                                setSearchText={setSearchText}
                                                searchOptions={searchOptions}
                                                defaultSearchColumn={'trmdNm'}
                                            />
                                        </div>
                                        <div style={{ flex: '0 0 auto' }}>
                                            <Space wrap>
                                                <PermissionButton type="primary" icon={<PlusOutlined/>} onClick={() => (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true)} disabled={permLoading || !canCreate} require={createTermData}>
                                                    신규생성
                                                </PermissionButton>
                                                <Popconfirm
                                                    title="선택된 항목 삭제"
                                                    description="선택된 항목들을 삭제하시겠습니까?"
                                                    onConfirm={deleteSelectedRowsAgGrid}
                                                    okText="삭제"
                                                    cancelText="취소"
                                                    disabled={permLoading || !canDeleteBatch}
                                                >
                                                    <PermissionButton danger icon={<DeleteOutlined/>} disabled={permLoading || !canDeleteBatch || deleting} loading={deleting} require={deleteTermDataBatch}>
                                                        선택 항목 삭제
                                                    </PermissionButton>
                                                </Popconfirm>
                                                <PermissionButton type="primary" icon={<UploadOutlined/>} onClick={() => (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true)} disabled={permLoading || !canUpload} require={createTermDataBatch} style={{backgroundColor: '#52c41a', borderColor: '#52c41a'}}>
                                                    엑셀 업로드
                                                </PermissionButton>
                                                <Button type="primary" icon={<DownloadOutlined/>} onClick={handleDownloadExcel} style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}>엑셀 다운로드</Button>
                                                <PermissionButton icon={<ReloadOutlined/>} onClick={() => (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList()} disabled={permLoading || !canRead} require={getTermDataList}>새로고침</PermissionButton>
                                            </Space>
                                        </div>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Card>

                    <div style={{width: '100%'}}>
                        <AgGrid
                            ref={gridRef}
                            rowData={getFilteredData()}
                            columnDefs={agGridColumnDefs}
                            defaultColDef={agGridDefaultColDef}
                            paginationPageSize={20}
                            paginationPageSizeSelector={AG_GRID_PAGE_SIZE_OPTIONS}
                            rowSelection="multiple"
                            enableCellTextSelection={true}
                            localeText={AG_GRID_LOCALE_TEXT}
                        />
                    </div>

                    <ExcelUploadModal
                        isOpen={isExcelModalOpen}
                        onClose={() => setIsExcelModalOpen(false)}
                        onSuccess={handleExcelUploadSuccess}
                        agGridColumnDefs={agGridColumnDefs}
                        agGridDefaultColDef={agGridDefaultColDef}
                        fieldMapping={['trmdNm', 'trmExpln', 'trmEngAbbrNm', 'domainNm', 'synm']}
                        uploadApi={createTermDataBatch}
                        excelGuide={'A: *용어명 | B: *설명 | C: 영문약어 | D: 도메인명 | E: 동의어'}
                        rowKey="trmDataId"
                    />

                    <TermDataCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={handleCreateSuccess} />
                </Card>
            )}
        </PermissionGate>
    );
}

export default TermDataList;
